import { CaretDownIcon } from "@phosphor-icons/react/dist/csr/CaretDown";
import { Button } from "@volca/ui/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@volca/ui/components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@volca/ui/components/ui/select";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import type { RuleOwners } from "@/profitability/settings/types";
import { SettingsGroup, SettingsRow } from "./settings-group";
import type { TemplateSettingsState } from "./use-settings-state";

const NOBODY = "none";
const OwnerSelect = ({
  label,
  value,
  people,
  nobody,
  disabled,
  onChange,
}: {
  label: string;
  value: string | null;
  people: RuleOwners["people"];
  nobody: string;
  disabled?: boolean;
  onChange: (owner: string | null) => void;
}) => {
  const items = {
    [NOBODY]: nobody,
    ...Object.fromEntries(
      people.map((person) => [person.id, person.full_name]),
    ),
  };
  return (
    <Select
      value={value ?? NOBODY}
      items={items}
      disabled={disabled}
      onValueChange={(next) => onChange(next === NOBODY ? null : String(next))}
    >
      <SelectTrigger size="sm" aria-label={label} className="w-full sm:w-48">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="pointer-coarse:[&_[data-slot=select-item]]:min-h-11">
        {Object.entries(items).map(([id, name]) => (
          <SelectItem key={id} value={id}>
            {name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export const OwnersGroup = ({
  state,
  noun,
}: {
  state: TemplateSettingsState;
  noun: string;
}) => {
  const { settings, editable, save, saving } = state;
  const owners = settings?.owners;
  const rules = settings?.rules ?? [];
  if (!owners || (!owners.values.length && !rules.length)) return null;
  const name = (id: string | null) =>
    owners.people.find((person) => person.id === id)?.full_name ?? null;
  const fallback = owners.field
    ? `Use ${(owners.field_label ?? "group").toLowerCase()} reviewer`
    : "Unassigned";
  const overridden = rules.filter((rule) => rule.owner_id);
  return (
    <SettingsGroup
      title="Assigned reviewers"
      description={`Reviewers receive their assigned ${noun} by email after a period is marked reviewed. Unassigned findings go to managers with review emails enabled.`}
    >
      {owners.values.map((entry) => (
        <SettingsRow
          key={entry.value}
          label={entry.value}
          value={!editable ? (name(entry.owner_id) ?? "Unassigned") : undefined}
          action={
            editable && (
              <OwnerSelect
                label={`Reviewer for ${entry.value}`}
                value={entry.owner_id}
                people={owners.people}
                nobody="Unassigned"
                disabled={saving}
                onChange={(owner) => {
                  void save({
                    owners: { values: { [entry.value]: owner } },
                  }).catch(() => undefined);
                }}
              />
            )
          }
        />
      ))}
      {rules.length > 0 && (
        <Collapsible>
          <CollapsibleTrigger
            render={
              <Button
                variant="ghost"
                className="h-auto w-full justify-between gap-2 rounded-none px-4 py-4"
              />
            }
          >
            <span>
              Rule overrides{overridden.length ? ` (${overridden.length})` : ""}
            </span>
            <CaretDownIcon aria-hidden className="in-data-open:rotate-180" />
          </CollapsibleTrigger>
          <CollapsibleContent keepMounted>
            <Text variant="caption" tone="muted" className="px-4 pb-3">
              Assign a rule to someone else. Other findings use the reviewer
              above.
            </Text>
            <Stack className="divide-y divide-line">
              {rules.map((rule) => (
                <SettingsRow
                  key={rule.id}
                  label={rule.label}
                  value={
                    !editable ? (name(rule.owner_id) ?? fallback) : undefined
                  }
                  action={
                    editable && (
                      <OwnerSelect
                        label={`Reviewer for ${rule.label}`}
                        value={rule.owner_id}
                        people={owners.people}
                        nobody={fallback}
                        disabled={saving}
                        onChange={(owner) => {
                          void save({
                            owners: { rules: { [rule.id]: owner } },
                          }).catch(() => undefined);
                        }}
                      />
                    )
                  }
                />
              ))}
            </Stack>
          </CollapsibleContent>
        </Collapsible>
      )}
    </SettingsGroup>
  );
};
